
import { defineComponent, reactive, ref, computed } from "vue-demi";
import { capitalizeString } from "@/utils/formatter";
import axios from "@/plugins/http";
import { API_ROUTES } from "@/utils/API";
import { SMUser } from "@/store";
import { emailRules, isEmailValid } from "@/utils/validator";

export default defineComponent({
  name: "SubscriptionForm",

  components: {
    Popup: () => import("@/modules/Pricing/Popup/Popup.vue"),
    FormsTemplate: () => import("@/modules/Pricing/Services/Forms/FormsTemplate.vue"),
  },

  props: {
    monthly: {
      type: Number,
      default: 0,
    },
    plan: {
      type: String as () => "yearly" | "monthly",
      default: "yearly",
    },
    tier: {
      type: String as () => "premium" | "standard",
      default: "premium",
    },
  },

  emits: ["ok", "cancel", "close"],

  setup(props, { emit }) {
    const data = reactive({
      service_type: "compliance_subscription",
      employer_name:
        `${SMUser.user?.title} ${SMUser.user?.first_name} ${SMUser.user.last_name}` || "",
      phone: `${SMUser.user?.phone}` || "",
      email: `${SMUser.user?.email}` || "",
      company_name: `${SMUser.user?.company?.CompanyProfile?.name}` || "",
      additional: {
        plan: props.plan,
        tier: props.tier,
        number_of_employees: "",
        notes: "",
      },
    });

    const tierOptionsPrepaidSL = [
      { label: "Premium", value: "premium" },
      { label: "Standard", value: "standard" },
      { label: "Basic", value: "basic" },
    ];

    const tierOptionsNoPrepaidSL = [
      { label: "Premium", value: "premium" },
      { label: "Standard", value: "standard" },
    ];

    const tierOptions = computed(() => {
      if (!SMUser.prepaidSL) return tierOptionsNoPrepaidSL;

      return tierOptionsPrepaidSL;
    });

    const planOptions = [
      { label: "Annual", value: "yearly" },
      { label: "Monthly", value: "monthly" },
    ];

    const isPremiumTierPreSelected = computed(() => props.tier === "premium");

    const title = computed(
      () => `${capitalizeString(data.additional.tier)} Subscription Request Form`
    );

    const isPopoverOpen = ref(true);

    const onOk = () => {
      emit("ok");
    };

    const onCancel = () => {
      isPopoverOpen.value = false;

      emit("cancel");
    };

    const onClose = () => {
      isPopoverOpen.value = false;

      emit("close");
    };

    const isFormComplete = computed(() => {
      const requiredFields = [
        data.employer_name,
        data.phone,
        data.email,
        data.company_name,
        data.additional.plan,
        data.additional.tier,
        data.additional.number_of_employees,
      ];

      return (
        isEmailValid.value && requiredFields.every((val) => !!val && String(val).trim().length > 0)
      );
    });

    const loading = ref(false);

    async function submitServiceRequest() {
      try {
        loading.value = true;

        await axios.post(API_ROUTES.SUBMIT_SERVICE_REQUEST, data);
      } catch (error) {
        console.log("SUBMIT_SERVICE_REQUEST failed", error);
      } finally {
        loading.value = false;

        onOk();
      }
    }

    const emailValidationRules = emailRules;

    return {
      data,
      isPopoverOpen,
      onOk,
      onCancel,
      onClose,
      isFormComplete,
      loading,
      submitServiceRequest,
      planOptions,
      tierOptions,
      title,
      emailValidationRules,
      isPremiumTierPreSelected,
    };
  },
});
